import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { assert, CustomError } from '../../errors';
import {
    AllFinancialEntitiesDocument,
    CreatePartnershipDocument,
    CurrentUserProfileDocument,
    InvitePartnerDocument,
    RelationshipStatus,
    SetFinancialEntityPermissionDocument,
    ShareStatus,
    UpdatePartnershipDocument,
} from '../../graphql/generated';
import { isValidEmail } from '../../validation';
import { useCurrentUser } from '../user';

interface FormValues {
    firstName?: string;
    lastName?: string;
    relationshipStatus?: RelationshipStatus | null;
    email?: string;
    accountSharingPreferences: Record<string, ShareStatus | undefined>;
}

export const useCreatePartnershipInviteFormValues = () => {
    const { partnerInfo } = useCurrentUser();
    const [formValues, setFormValue] = useState<FormValues>({
        firstName:
            partnerInfo?.firstName || partnerInfo?.preferredName || undefined,
        lastName: partnerInfo?.lastName || undefined,
        relationshipStatus: partnerInfo?.relationshipStatus || undefined,
        email: partnerInfo?.email || undefined,
        accountSharingPreferences: {},
    });

    return { formValues, setFormValue };
};

export const useCreatePartnershipInvite = ({
    formValues,
    onComplete,
    onError,
}: {
    formValues: FormValues;
    onComplete: VoidFunction;
    onError: (e: unknown) => void;
}) => {
    const [createPartnership] = useMutation(CreatePartnershipDocument);
    const [updatePartnership] = useMutation(UpdatePartnershipDocument);
    const [setFinancialEntityPermission] = useMutation(
        SetFinancialEntityPermissionDocument
    );
    const [invitePartner] = useMutation(InvitePartnerDocument, {
        refetchQueries: [
            AllFinancialEntitiesDocument,
            CurrentUserProfileDocument,
        ],
    });
    const [isLoading, setIsLoading] = useState(false);
    const isDisabled = getIsDisabled(formValues);
    let { activePartnershipId } = useCurrentUser();

    const submit = async () => {
        if (isDisabled) return;

        assert(
            formValues.firstName &&
                formValues.lastName &&
                formValues.email &&
                formValues.relationshipStatus,

            'Expected all fields to be populated'
        );

        setIsLoading(true);

        // TODO: This is a lot of separate API calls - consider introducing a new mutation that combines
        // creating/updating partnership, setting permissions, and inviting partner
        try {
            if (activePartnershipId) {
                await updatePartnership({
                    variables: {
                        partnershipId: activePartnershipId,
                        relationshipStatus: formValues.relationshipStatus,
                        partnerEmail: formValues.email,
                        partnerFirstName: formValues.firstName,
                        partnerLastName: formValues.lastName,
                    },
                });
            } else {
                const response = await createPartnership({
                    variables: {
                        relationshipStatus: formValues.relationshipStatus,
                        partnerEmail: formValues.email,
                        partnerFirstName: formValues.firstName,
                        partnerPreferredName: formValues.firstName,
                        partnerLastName: formValues.lastName,
                    },
                });

                if (response.data?.createPartnership?.id) {
                    activePartnershipId = response.data.createPartnership.id;
                } else {
                    throw new CustomError('Failed to create partnership', {
                        errors: response.errors,
                    });
                }
            }

            // Set sharing preferences for each financial entity
            for (const [financialEntityId, shareStatus] of Object.entries(
                formValues.accountSharingPreferences
            )) {
                if (!shareStatus) continue;
                await setFinancialEntityPermission({
                    variables: {
                        partnershipId: activePartnershipId,
                        financialEntityId,
                        shareStatus,
                    },
                });
            }

            await invitePartner();

            onComplete();
        } catch (e) {
            onError(e);
        } finally {
            setIsLoading(false);
        }
    };

    return { submit, isLoading, isDisabled };
};

const getIsDisabled = (formValues: FormValues) =>
    Object.values(formValues).some((value) => !value) ||
    !isValidEmail(formValues.email);
